import React from 'react'
import { Box, TableColumn, TextBody } from '@ally/metronome-ui'
import { asCurrency, maskAccountNumber } from '@ally/aos-utils'
import { Account } from '@ally/data-types-afg-accounts'
import { Cell, Row } from 'react-table'
import {
  formatBalance,
  formatClosedDate,
  formatMaturityDate,
  formatPercentage,
} from '../../../utils'
import { AccountDetailsLink } from '../../Shared'
import { DashboardView } from '../../../Customizations/types'

interface GetColumnsProps {
  planType: string
  view: DashboardView
  isLgDown: boolean
  hasStaleData: boolean
  isProductNameChangeEnabled: boolean
}

interface GetFooterProps {
  view: DashboardView
}

interface CellProps {
  cell: Cell<Account>
  row: Row<Account>
}

interface FooterProps {
  rows: Row<Account>[]
}

export const getColumns = ({
  planType,
  view,
  isLgDown,
  hasStaleData,
  isProductNameChangeEnabled,
}: GetColumnsProps): TableColumn<Account>[] => {
  const isDefaultView = view === DashboardView.DEFAULT || isLgDown

  const accountNicknameHeader = isProductNameChangeEnabled
    ? `${planType} IRA`
    : `${planType} IRA Plan`

  const columns = {
    accountNickname: {
      Header: accountNicknameHeader,
      accessor: 'nickName',
      alignment: 'left',
      isSortable: true,
      Cell: ({ cell, row }: CellProps): React.ReactNode | string => {
        const accountTypeLowerCase = row.original.type.toLowerCase()
        return (
          <>
            <AccountDetailsLink
              account={row.original}
              text={cell.value}
              allytm={`deposit-${accountTypeLowerCase}Account-IRA-${planType}`}
              hasStaleData={hasStaleData}
            />
            <Box display="inline-block" aria-hidden="true">
              {maskAccountNumber(row.original.accountNumber)}
            </Box>
          </>
        )
      },
    },
    currentBalance: {
      Header: 'Current',
      accessor: 'balance.current',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string => {
        const acctClosingDate = row.original.closeDate || ''

        if (!acctClosingDate && value === 0) {
          return (
            <TextBody tag="span" size="sm">
              $0.00
            </TextBody>
          )
        }

        return acctClosingDate
          ? formatClosedDate(acctClosingDate)
          : formatBalance(value)
      },
    },
    interest: {
      Header: 'Interest',
      // accessor is not actually being used but cannot be an empty string
      accessor: 'interest-not-real-accessor',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row } }: CellProps): React.ReactNode | string => {
        const { closeDate, type, interest } = row.original

        const interestType = type === 'CDA' ? 'ACCRUED' : 'YTD'
        // istanbul ignore next
        const displayedInterest = (interest?.totalAccrued || interest?.ytd) ?? 0
        // if they have closed account leave blank
        if (closeDate) {
          return null
        }
        return `${formatBalance(displayedInterest)} ${interestType}`
      },
    },
    cdMaturityDate: {
      Header: 'Matures',
      accessor: 'meta.cdMaturityDate',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row } }: CellProps): React.ReactNode | string => {
        const { closeDate, meta } = row.original
        // if they have a closed account leave blank
        if (closeDate) {
          return null
        }
        if (meta?.cdMaturityDate) {
          return formatMaturityDate(meta.cdMaturityDate)
        }
        return '-'
      },
    },
    apy: {
      Header: 'Annual Percentage Yield',
      accessor: 'interest.apy',
      alignment: 'right',
      isSortable: true,
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string => {
        const { closeDate } = row.original
        // if they have a closed account leave blank
        if (closeDate) {
          return null
        }
        return formatPercentage(value)
      },
    },
    empty: {
      Header: '',
      accessor: 'empty',
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string =>
        null,
    },
  }

  const tileViewCols = [
    columns.accountNickname,
    columns.currentBalance,
    columns.interest,
    columns.empty,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountNickname,
    columns.currentBalance,
    columns.interest,
    columns.cdMaturityDate,
    columns.apy,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}

export const getFooter = ({ view }: GetFooterProps): TableColumn<Account>[] => {
  const isDefaultView = view === DashboardView.DEFAULT
  const columns = {
    accountNickname: {
      Header: '',
      Footer: 'Total',
      accessor: 'nickName',
      alignment: 'left',
      isSortable: false,
      Cell: (): null => null,
    },
    currentBalance: {
      Header: '',
      accessor: 'balance.current',
      alignment: 'right',
      isSortable: false,
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () =>
            rows.reduce(
              (sum, row) => (row.values['balance.current'] ?? 0) + sum,
              0,
            ),
          [rows],
        )

        return asCurrency(total)
      },
    },
    interest: {
      Header: '',
      // accessor is not actually being used but cannot be an empty string
      accessor: 'interest-not-real-accessor',
      alignment: 'right',
      isSortable: false,
      Footer: ({ rows }: FooterProps): React.ReactNode => {
        const total = React.useMemo(
          () =>
            rows.reduce((sum, row) => {
              if (row.original.interest?.totalAccrued) {
                return row.original.interest.totalAccrued + sum
              }
              // istanbul ignore next
              return (row.original.interest?.ytd ?? 0) + sum
            }, 0),
          [rows],
        )
        return asCurrency(total)
      },
    },
    cdMaturityDate: {
      Header: '',
      accessor: 'meta.cdMaturityDate',
      alignment: 'right',
      isSortable: false,
      Cell: (): null => null,
    },
    apy: {
      Header: '',
      accessor: 'interest.apy',
      alignment: 'right',
      isSortable: false,
      Cell: (): null => null,
    },
    empty: {
      Header: '',
      accessor: 'empty',
      Cell: ({ cell: { row, value } }: CellProps): React.ReactNode | string =>
        null,
    },
  }

  const tileViewCols = [
    columns.accountNickname,
    columns.currentBalance,
    columns.cdMaturityDate,
    columns.empty,
  ] as TableColumn<Account>[]
  const defaultViewCols = [
    columns.accountNickname,
    columns.currentBalance,
    columns.interest,
    columns.cdMaturityDate,
    columns.apy,
  ] as TableColumn<Account>[]

  return isDefaultView ? defaultViewCols : tileViewCols
}
